import React, { useMemo, useRef, useEffect, useState, useCallback } from "react";
import { Table } from "antd";
import { useGridNavigation } from "./useGridNavigation";
import { useRowColorHotkey } from "./useRowColorHotkey";
import { useUserRegistry } from "../context/RegistryContexts/RegistryUserContext";

// Simulate a native click (mousedown + mouseup + click)
const simulateClick = (el) => {
   if (!el) return;
   const opts = { bubbles: true, cancelable: true, view: window };
   el.dispatchEvent(new MouseEvent("mousedown", opts));
   el.dispatchEvent(new MouseEvent("mouseup", opts));
   el.dispatchEvent(new MouseEvent("click", opts));
};

// dropdown element in a portal (#dropdown-root)
export const DROPDOWN_ITEM_SELECTOR = [
   "button",
   "[data-testid='dropdown-option-button']",
   ".dropdown-button-item",
   "[role='menuitem']",
   "[role='option']",
   "a[href]",
   ".ant-dropdown-menu-item",
   ".ant-dropdown-menu-title-content",
   ".ant-select-item",
   ".ant-select-item-option",
   "[data-value]",
   "[tabindex]:not([tabindex='-1'])",
].join(",");

const isDropdownItemEvent = (target) => {
   if (!(target instanceof Element)) return false;
   const popup = getAnyDropdownContainer();
   if (!popup) return false;
   if (!popup.contains(target)) return false;
   return !!target.closest(DROPDOWN_ITEM_SELECTOR);
};

const getDropdownRoot = () => document.getElementById("dropdown-root") || null;
const getAnyDropdownContainer = () => {
   const root = getDropdownRoot();
   if (!root) return null;
   return (
      root.querySelector(".items-container") ||
      root.querySelector('[role="menu"]') ||
      root.querySelector("[data-bp2-dropdown]") ||
      null
   );
};

const getDropdownContainer = (popupId) => {
   if (popupId) {
      const byId = document.getElementById(popupId);
      if (byId) return byId;
   }
   return getAnyDropdownContainer();
};

const getMenuItems = (popupEl) =>
   Array.from(popupEl.querySelectorAll(DROPDOWN_ITEM_SELECTOR)).filter(
      (el) => !el.hasAttribute("disabled") && el.tabIndex !== -1,
   );

const isAnyDropdownOpen = () => !!getAnyDropdownContainer();

const focusFirstDropdownItem = ({ popupId, fallbackEl, maxTries = 14 } = {}) => {
   let tries = 0;
   const step = () => {
      const popup = getDropdownContainer(popupId);
      if (popup) {
         const items = getMenuItems(popup);
         if (items.length) {
            items[0].focus({ preventScroll: true });
            return;
         }
      }
      tries += 1;
      if (tries < maxTries) requestAnimationFrame(step);
      else if (fallbackEl?.focus) fallbackEl.focus({ preventScroll: true });
   };
   requestAnimationFrame(step);
};

const findRowEl = (cellEl) => cellEl?.closest?.("tr, [role='row']") || null;

const findActionTriggerInRow = (rowEl) =>
   rowEl?.querySelector?.(
      "[data-actions-trigger], .bp2-actions-dropdown-btn, .table-actions-trigger, button[aria-haspopup='menu']",
   ) || null;

const getCellWrapperAndCoords = (triggerEl) => {
   const wrapper = triggerEl?.closest?.('[data-grid-cell="true"]') || null;
   if (!wrapper) return { wrapper: null, row: null, col: null };
   const rowAttr = wrapper.getAttribute("data-row");
   const colAttr = wrapper.getAttribute("data-col");
   const row = Number(rowAttr);
   const col = Number(colAttr);
   return {
      wrapper,
      row: Number.isFinite(row) ? row : null,
      col: Number.isFinite(col) ? col : null,
   };
};

const ListGridNavWrapper = ({
   dataSource,
   columns,
   tableProps = {},
   fullColumns,
   formId,
   handleListResponse,
   listId,
   onRowClick,
   mapIsDisplay,
}) => {
   const rowsCount = Array.isArray(dataSource) ? dataSource.length : 0;
   const colsCount = Array.isArray(columns) ? columns.length : 0;

   const tableRootRef = useRef(null);

   const { isNavGridEnabled } = useUserRegistry();

   // Local open-menu state & guards
   const openMenuRef = useRef({
      row: null,
      trigger: null,
      open: false,
      popupId: null,
      root: null,
      keepCell: null,
   });
   const [isMenuOpen, setIsMenuOpen] = useState(false);
   const suppressToggleUntilRef = useRef(0);

   useEffect(() => {
      const root = tableRootRef.current;
      if (!root) return;
      const setLast = () => {
         window.__lastGridRoot = root;
      };
      root.addEventListener("focusin", setLast, true);
      root.addEventListener("mousedown", setLast, true);
      return () => {
         root.removeEventListener("focusin", setLast, true);
         root.removeEventListener("mousedown", setLast, true);
      };
   }, []);

   useEffect(() => {
      const root = tableRootRef.current;
      if (!root) return;

      const onFocusOut = (e) => {
         const next = e.relatedTarget;
         if (!next || !root.contains(next)) {
            clearGridFocusAndHighlight(root);
         }
      };

      root.addEventListener("focusout", onFocusOut, true);
      return () => root.removeEventListener("focusout", onFocusOut, true);
   }, []);

   const getFocusedGridCell = useCallback(() => {
      return tableRootRef.current?.querySelector('[data-grid-cell="true"][tabindex="0"]') || null;
   }, []);

   const setGridActive = (rootEl, active) => {
      if (!rootEl) return;
      rootEl.setAttribute("data-bp2-grid-active", active ? "true" : "false");
   };

   const clearGridFocusAndHighlight = (rootEl) => {
      if (!rootEl) return;

      const run = () => {
         rootEl
            .querySelectorAll(".bp2-active-cell")
            .forEach((el) => el.classList.remove("bp2-active-cell"));

         rootEl
            .querySelectorAll('[data-grid-cell="true"]')
            .forEach((el) => el.setAttribute("tabindex", "-1"));

         setGridActive(rootEl, false);
      };

      run();
      requestAnimationFrame(run);
      setTimeout(run, 0);
   };

   const clearActiveCellsInGrid = (rootEl) => {
      if (!rootEl) return;
      rootEl
         .querySelectorAll(".bp2-active-cell")
         .forEach((el) => el.classList.remove("bp2-active-cell"));
   };

   const moveFocusToAdjacentWidget = (rootEl, direction) => {
      clearGridFocusAndHighlight(rootEl);
      const currentOuter = rootEl?.closest?.('[data-table-widget="true"]') ?? null;
      const tables = Array.from(document.querySelectorAll('[data-table-widget="true"]'));
      if (!currentOuter || tables.length === 0) return;

      const idx = tables.indexOf(currentOuter);
      if (idx === -1) return;

      let targetIdx = idx + direction;
      if (targetIdx < 0) targetIdx = tables.length - 1;
      if (targetIdx >= tables.length) targetIdx = 0;

      const nextTable = tables[targetIdx];

      const targetCell =
         nextTable.querySelector('[data-grid-cell="true"][tabindex="0"]') ||
         nextTable.querySelector('[data-grid-cell="true"]');

      const navEl = nextTable.hasAttribute("data-bp2-grid-id")
         ? nextTable
         : nextTable.querySelector("[data-bp2-grid-id]");
      const nextId = navEl?.getAttribute("data-bp2-grid-id") ?? nextTable.__grid_id;
      if (nextId) window.__activeGridId = nextId;

      document.activeElement instanceof HTMLElement && document.activeElement.blur();

      (navEl ?? nextTable).focus?.({ preventScroll: false });
      requestAnimationFrame(() => {
         if (targetCell) targetCell.focus({ preventScroll: true });
         else (navEl ?? nextTable).focus?.({ preventScroll: true });
      });
   };

   const resolveOwnerRoot = () => {
      const st = openMenuRef.current;
      return (
         st.root ||
         st.trigger?.closest?.('[data-table-widget="true"]') ||
         tableRootRef.current ||
         window.__lastGridRoot ||
         null
      );
   };

   const closeOpenMenu = useCallback((opts = {}) => {
      const { keepFocusEl = null } = opts;
      const { open, trigger, row } = openMenuRef.current;
      if (!(open && trigger && document.contains(trigger))) return;

      const currentTrigger = trigger;

      // reset
      openMenuRef.current = {
         row: null,
         trigger: null,
         open: false,
         popupId: null,
         root: null,
         keepCell: null,
      };
      setIsMenuOpen(false);

      // Guards: allow immediate re-open on pure Shift; add small suppress on others
      suppressToggleUntilRef.current = Date.now() + 220;

      // close dropdown
      document.dispatchEvent(new CustomEvent("dropdown/close", { detail: { rowId: row } }));

      // hide dropdown
      setTimeout(() => {
         if (isAnyDropdownOpen() && currentTrigger && document.contains(currentTrigger)) {
            try {
               simulateClick(currentTrigger);
            } catch {}
         }
      }, 30);

      // Restore focus back to grid cell
      requestAnimationFrame(() => {
         if (keepFocusEl?.focus)
            requestAnimationFrame(() => keepFocusEl.focus({ preventScroll: true }));
      });
   }, []);

   useEffect(() => {
      const onEsc = (e) => {
         if (e.key !== "Escape") return;
         if (!isAnyDropdownOpen()) return;

         e.preventDefault();
         e.stopPropagation();
         e.stopImmediatePropagation?.();

         const keep = openMenuRef.current.keepCell || getFocusedGridCell();
         closeOpenMenu({ reason: "esc", keepFocusEl: keep });
      };

      const onOutside = (e) => {
         const { open, trigger } = openMenuRef.current;
         if (!open) return;
         const popupEl = getAnyDropdownContainer();
         if (trigger && (e.target === trigger || trigger.contains(e.target))) return;
         if (popupEl && popupEl.contains(e.target)) return;

         // if click target is on the other table, clear focus in old table
         const ownerRoot = resolveOwnerRoot?.() || openMenuRef.current.root || null;
         const targetTable = e.target?.closest?.('[data-table-widget="true"]') || null;
         if (ownerRoot && targetTable && targetTable !== ownerRoot) {
            // remove active class and tabindex=0 from old table
            ownerRoot
               .querySelectorAll(".bp2-active-cell")
               .forEach((el) => el.classList.remove("bp2-active-cell"));
            ownerRoot
               .querySelectorAll('[data-grid-cell="true"][tabindex="0"]')
               .forEach((el) => el.setAttribute("tabindex", "-1"));
            ownerRoot.setAttribute("data-bp2-grid-active", "false");
         }

         closeOpenMenu({ reason: "outside", keepFocusEl: null });
      };

      document.addEventListener("keydown", onEsc, true);
      document.addEventListener("mousedown", onOutside, true);
      return () => {
         document.removeEventListener("keydown", onEsc, true);
         document.removeEventListener("mousedown", onOutside, true);
      };
   }, [closeOpenMenu, getFocusedGridCell]);

   useEffect(() => {
      const root = tableRootRef.current;
      if (!root) return;

      const onTriggerDown = (e) => {
         const t = e.target.closest(
            "[data-actions-trigger], .bp2-actions-dropdown-btn, .table-actions-trigger, button[aria-haspopup='menu']",
         );
         if (!t || !root.contains(t)) return;

         openMenuRef.current.root = root;
         openMenuRef.current.trigger = t;

         const { wrapper } = getCellWrapperAndCoords(t);
         openMenuRef.current.keepCell = wrapper || null;

         requestAnimationFrame(() => {
            openMenuRef.current.open = !!isAnyDropdownOpen();
         });
      };

      root.addEventListener("mousedown", onTriggerDown, true);
      return () => root.removeEventListener("mousedown", onTriggerDown, true);
   }, []);

   // After click set flag open
   useEffect(() => {
      const root = tableRootRef.current;
      if (!root) return;

      const onTriggerClick = (e) => {
         const t = e.target?.closest(
            "[data-actions-trigger], .bp2-actions-dropdown-btn, .table-actions-trigger, button[aria-haspopup='menu']",
         );
         if (!t || !root.contains(t)) return;

         // save root cell
         const { wrapper } = getCellWrapperAndCoords(t);
         openMenuRef.current.root = root;
         openMenuRef.current.trigger = t;
         openMenuRef.current.keepCell = wrapper || null;

         if (isAnyDropdownOpen()) {
            try {
               document.dispatchEvent(
                  new CustomEvent("bp2:dropdown/force-close", {
                     detail: { rowId: openMenuRef.current.row ?? null },
                  }),
               );
            } catch {}

            openMenuRef.current.open = false;
            setIsMenuOpen(false);

            const back = openMenuRef.current.keepCell || t;
            requestAnimationFrame(() => back?.focus?.({ preventScroll: true }));
            e.preventDefault();
            e.stopPropagation();
            return;
         }

         let tries = 0;
         const pump = () => {
            const popup = getAnyDropdownContainer();
            if (popup) {
               openMenuRef.current.open = true;
               setIsMenuOpen(true);

               try {
                  document.dispatchEvent(new CustomEvent("bp2:dropdown/open"));
               } catch {}

               focusFirstDropdownItem({ fallbackEl: wrapper || t, maxTries: 60 });
               return;
            }
            if (++tries < 80) requestAnimationFrame(pump);
         };

         requestAnimationFrame(pump);
      };

      root.addEventListener("click", onTriggerClick, true);
      return () => root.removeEventListener("click", onTriggerClick, true);
   }, []);

   useEffect(() => {
      if (!isMenuOpen) return;

      // save last active table
      const prevActive = window.__activeGridId;

      window.__activeGridId = "__dropdown_lock__";

      return () => {
         if (window.__activeGridId === "__dropdown_lock__") {
            window.__activeGridId = prevActive;
         }
      };
   }, [isMenuOpen]);

   const hardBlurGrid = (rootEl) => {
      if (!rootEl) return;
      const activeCell = rootEl.querySelector('[data-grid-cell="true"][tabindex="0"]');
      if (activeCell) {
         activeCell.setAttribute("tabindex", "-1");
         if (activeCell instanceof HTMLElement) activeCell.blur();
      }

      rootEl
         .querySelectorAll(".bp2-active-cell")
         .forEach((el) => el.classList.remove("bp2-active-cell"));
      if (rootEl instanceof HTMLElement) rootEl.blur();
   };

   useEffect(() => {
      const onDropdownKeys = (e) => {
         const popup = getAnyDropdownContainer();
         if (!popup) return;

         if (!openMenuRef.current.open) return;

         const inPopup =
            popup.contains(e.target) || popup.contains(document.activeElement || document.body);
         if (!inPopup) return;

         const key = e.key;

         if (key === "Shift") {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation?.();
            return;
         }

         // Close on Tab / Shift+Tab
         if (key === "Tab") {
            e.preventDefault();
            e.stopPropagation();
            e.stopImmediatePropagation?.();
            window.__gridTabTransition = true;

            const currentRoot = resolveOwnerRoot();

            closeOpenMenu({
               reason: e.shiftKey ? "tab+shift" : "tab",
            });

            // remove focus from next/prev table
            if (currentRoot) {
               hardBlurGrid(currentRoot);
               clearActiveCellsInGrid(currentRoot);
               requestAnimationFrame(() => {
                  moveFocusToAdjacentWidget(currentRoot, e.shiftKey ? -1 : +1);
                  window.__gridTabTransition = false;
               });
            } else {
               document.querySelectorAll('[data-table-widget="true"]').forEach(hardBlurGrid);
               const any = document.querySelector('[data-table-widget="true"]');
               if (any) moveFocusToAdjacentWidget(any, e.shiftKey ? -1 : +1);
               window.__gridTabTransition = false;
            }
            return;
         }

         // Arrow navigation within dropdown
         const navKeys = ["ArrowDown", "ArrowUp", "ArrowLeft", "ArrowRight"];
         if (!navKeys.includes(key)) return;

         e.preventDefault();
         e.stopPropagation();
         e.stopImmediatePropagation?.();

         const items = getMenuItems(popup);
         if (!items.length) return;

         let idx = items.indexOf(document.activeElement);
         if (idx === -1) idx = 0;

         if (key === "ArrowDown")
            return items[(idx + 1) % items.length].focus({ preventScroll: true });
         if (key === "ArrowUp")
            return items[(idx - 1 + items.length) % items.length].focus({ preventScroll: true });

         if (key === "ArrowLeft" || key === "ArrowRight") return;
      };

      document.addEventListener("keydown", onDropdownKeys, true);
      return () => document.removeEventListener("keydown", onDropdownKeys, true);
   }, [closeOpenMenu, getFocusedGridCell]);

   useEffect(() => {
      const asyncClose = () => {
         if (!openMenuRef.current.open) return;
         const { trigger } = openMenuRef.current;
         const keep = openMenuRef.current.keepCell || getFocusedGridCell();

         // double RAF - let the library perform an onClick on the option
         requestAnimationFrame(() => {
            requestAnimationFrame(() => {
               if (trigger && document.contains(trigger)) {
                  try {
                     simulateClick(trigger);
                  } catch {}
               }
               // clear and restore focus
               closeOpenMenu({ reason: "select", keepFocusEl: keep });
            });
         });
      };

      const onItemClick = (e) => {
         if (openMenuRef.current.open && isDropdownItemEvent(e.target)) asyncClose();
      };
      const onItemKey = (e) => {
         if (!openMenuRef.current.open) return;
         if ((e.key === "Enter" || e.key === " ") && isDropdownItemEvent(e.target)) asyncClose();
      };
      const onItemPointerUp = (e) => {
         if (openMenuRef.current.open && isDropdownItemEvent(e.target)) asyncClose();
      };

      document.addEventListener("click", onItemClick, true);
      document.addEventListener("keydown", onItemKey, true);
      document.addEventListener("pointerup", onItemPointerUp, true);
      document.addEventListener("mouseup", onItemPointerUp, true); // fallback

      return () => {
         document.removeEventListener("click", onItemClick, true);
         document.removeEventListener("keydown", onItemKey, true);
         document.removeEventListener("pointerup", onItemPointerUp, true);
         document.removeEventListener("mouseup", onItemPointerUp, true);
      };
   }, [closeOpenMenu, getFocusedGridCell]);

   // Close dropdown on Shift (keyup) while dropdown is open
   useEffect(() => {
      const onShiftUp = (e) => {
         if (e.key !== "Shift") return;
         if (!openMenuRef.current.open) return;

         e.preventDefault();
         e.stopPropagation();
         e.stopImmediatePropagation?.();

         const keep = openMenuRef.current.keepCell || getFocusedGridCell();
         closeOpenMenu({ reason: "shift", keepFocusEl: keep });
      };

      document.addEventListener("keyup", onShiftUp, true);
      return () => document.removeEventListener("keyup", onShiftUp, true);
   }, [closeOpenMenu, getFocusedGridCell]);

   // when opened from a non-actions cell, move highlight to actions
   const nav = useGridNavigation({
      rowsCount,
      colsCount,
      onToggleActions: ({ row, col, cellEl }) => {
         if (Date.now() < suppressToggleUntilRef.current) {
            cellEl?.focus?.({ preventScroll: true });
            return;
         }

         const rowEl = findRowEl(cellEl);
         const trigger = findActionTriggerInRow(rowEl);

         if (!trigger) {
            cellEl?.focus?.({ preventScroll: true });
            return;
         }

         // Move grid highlight to the Actions cell BEFORE opening
         const {
            wrapper: actionsCellWrapper,
            row: actionsRow,
            col: actionsCol,
         } = getCellWrapperAndCoords(trigger);
         const baseCell = actionsCellWrapper || cellEl;

         if (actionsRow != null && actionsCol != null) {
            nav.setActive?.({ row: actionsRow, col: actionsCol });
            nav.focusCell?.(actionsRow, actionsCol);
         }

         openMenuRef.current.root =
            trigger.closest?.('[data-table-widget="true"]') || tableRootRef.current;
         openMenuRef.current.keepCell = baseCell || null;

         // Focus trigger and open dropdown
         trigger.focus?.({ preventScroll: true });
         simulateClick(trigger);

         requestAnimationFrame(() => {
            const openNow = isAnyDropdownOpen();
            openMenuRef.current = openNow
               ? {
                    row,
                    trigger,
                    open: true,
                    popupId: null,
                    root: openMenuRef.current.root || tableRootRef.current,
                    keepCell: openMenuRef.current.keepCell || baseCell || null,
                 }
               : {
                    row: null,
                    trigger: null,
                    open: false,
                    popupId: null,
                    root: null,
                    keepCell: null,
                 };
            setIsMenuOpen(openNow);

            if (openNow) {
               focusFirstDropdownItem({ fallbackEl: baseCell });
            } else {
               baseCell?.focus?.({ preventScroll: true });
            }
         });
      },
   });

   const decoratedColumns = useMemo(() => {
      return (columns || []).map((col, colIdx) => {
         const baseRender = col.render || ((text) => (text != null ? String(text) : ""));
         return {
            ...col,
            render: (text, record, rowIndex) => (
               <span
                  {...(isNavGridEnabled === "1" ? nav.getCellProps(rowIndex, colIdx) : {})}
                  className="bp2-cell-focus-target"
                  style={{
                     display: "block",
                     width: "100%",
                     height: "100%",
                     minWidth: 0,
                     outline: "none",
                  }}
               >
                  {baseRender(text, record, rowIndex)}
               </span>
            ),
         };
      });
   }, [columns, nav, isNavGridEnabled]);

   const colorHotkey = useRowColorHotkey({
      formId,
      listId,
      handleListResponse,
      columns: fullColumns,
      dataSource,
      isMenuOpen,
   });

   const [selectedRowIndices, setSelectedRowIndices] = useState([]);

   // Stable rowKey for AntD
   const safeTableProps = {
      rowKey: tableProps.rowKey || ((record, idx) => record?.key ?? record?._sysRecordId ?? idx),
      ...tableProps,
      onRow: (record, rowIndex) => {
         if (!mapIsDisplay) return;
         const existingOnRow = tableProps.onRow?.(record, rowIndex) || {};
         const existingClassName = existingOnRow.className || "";
         const isSelected = selectedRowIndices.includes(rowIndex);
         const selectedClass = isSelected ? "bp2-selected-row" : "";
         return {
            ...existingOnRow,
            className: `${existingClassName} ${selectedClass}`.trim(),
            onClick: (e) => {
               existingOnRow.onClick?.(e);
               setSelectedRowIndices((prev) => {
                  if (prev.includes(rowIndex)) {
                     return prev.filter((idx) => idx !== rowIndex);
                  } else {
                     return [...prev, rowIndex];
                  }
               });
               onRowClick?.(record, rowIndex, e, !isSelected);
            },
         };
      },
   };

   // Keep grid highlight when focus moves into the dropdown portal
   const navProps = nav.getTableProps();
   const hotkeyProps = colorHotkey.getHotkeyProps();

   const wrappedNavProps = {
      ...navProps,
      onBlur: (e) => {
         const ddRoot = document.getElementById("dropdown-root");
         if (ddRoot && ddRoot.contains(e.relatedTarget)) {
            e.preventDefault?.();
            e.stopPropagation?.();
            return;
         }
         navProps.onBlur?.(e);
      },
   };

   const assignRef = (ref, el) => {
      if (!ref) return;
      if (typeof ref === "function") {
         try {
            ref(el);
         } catch {}
      } else {
         try {
            ref.current = el;
         } catch {}
      }
   };

   const mergedRef = (el) => {
      tableRootRef.current = el;
      assignRef(wrappedNavProps.ref, el);
      assignRef(hotkeyProps.ref, el);
   };

   const { ref: _dropRef, ...restWrappedNavProps } = wrappedNavProps;
   const { ref: _hotkeyRef, ...restHotkeyProps } = hotkeyProps;

   return (
      <div
         ref={mergedRef}
         data-table-widget="true"
         data-table-kind="list"
         {...restWrappedNavProps}
         {...restHotkeyProps}
         className="list-widget-table"
      >
         <Table {...safeTableProps} dataSource={dataSource} columns={decoratedColumns} />
      </div>
   );
};

export default ListGridNavWrapper;
